import React, { useEffect, useState } from 'react';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Typography, TablePagination, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Divider, useTheme, useMediaQuery, Card, CardContent, Stack, Chip, Tooltip, InputAdornment } from '@mui/material';
import { Delete as DeleteIcon, AttachFile as AttachFileIcon, Edit as EditIcon, Add as AddIcon } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import * as XLSX from 'xlsx';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import proveedorService, { Proveedor } from '../../services/proveedorService';
import { getDeleteErrorMessage } from '../../utils/maestroDeleteHelper';
import { useNavigate } from 'react-router-dom';

interface ProveedorPagedResult {
  items: Proveedor[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

const Proveedores: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [proveedoresData, setProveedoresData] = useState<ProveedorPagedResult>({ items: [], totalCount: 0, pageNumber: 0, pageSize: 10, totalPages: 0 });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [openImportar, setOpenImportar] = useState(false);
  const [validacionImport, setValidacionImport] = useState<{
    payloadsValidos: any[];
    errores: { fila: number; mensaje: string }[];
    advertencias: { fila: number; mensaje: string }[];
    totalFilas: number;
  } | null>(null);
  const [importando, setImportando] = useState(false);

  const getTipoIvaChip = (tipoIva?: string) => {
    if (!tipoIva) {
      return {
        label: 'Sin dato',
        bgColor: '#9e9e9e',
        textColor: '#ffffff'
      };
    }

    const normalized = tipoIva.toUpperCase();

    if (normalized === 'I' || normalized.includes('RESPONSABLE')) {
      return {
        label: 'Resp. Insc.',
        bgColor: '#15803d',
        textColor: '#ffffff'
      };
    }

    if (normalized === 'M' || normalized.includes('MONOTRIBUTO')) {
      return {
        label: 'Monotributo',
        bgColor: '#4caf50',
        textColor: '#ffffff'
      };
    }

    if (normalized === 'E' || normalized.includes('EXENTO')) {
      return {
        label: 'Exento',
        bgColor: '#ff9800',
        textColor: '#ffffff'
      };
    }

    if (normalized === 'C' || normalized.includes('CONSUMIDOR')) {
      return {
        label: 'Consumidor Final',
        bgColor: '#9c27b0',
        textColor: '#ffffff'
      };
    }

    if (normalized === 'P' || normalized.includes('PRESUPUESTO')) {
      return {
        label: 'Presupuesto',
        bgColor: '#9c27b0',
        textColor: '#ffffff'
      };
    }

    return {
      label: tipoIva,
      bgColor: '#757575',
      textColor: '#ffffff'
    };
  };

  const getTipoProveedorChip = (tipo?: string) => {
    if (!tipo) {
      return {
        label: 'Sin dato',
        bgColor: '#9e9e9e',
        textColor: '#ffffff'
      };
    }

    const normalized = tipo.toUpperCase();

    if (normalized === 'C' || normalized.includes('COMPRA')) {
      return {
        label: 'Compra',
        bgColor: '#2196f3',
        textColor: '#ffffff'
      };
    }

    if (normalized === 'G' || normalized.includes('GENERAL')) {
      return {
        label: 'General',
        bgColor: '#4caf50',
        textColor: '#ffffff'
      };
    }

    return {
      label: tipo,
      bgColor: '#757575',
      textColor: '#ffffff'
    };
  };

  const fetchProveedores = async (pageNumber: number, pageSize: number, search?: string) => {
    const rawSearch = (search ?? '').trim();
    const compact = rawSearch.replace(/\s+/g, '');
    const digitsOnly = compact.replace(/\D/g, '');
    // Solo buscar por nroProveedor directo si es un número corto (no CUIT ni búsqueda parcial larga)
    const isDirectNroProveedorSearch =
      compact !== '' && /^\d+$/.test(compact) && digitsOnly.length <= 6;
    if (isDirectNroProveedorSearch) {
      try {
        const nro = Number(compact);
        const prov = await proveedorService.getProveedor(nro);
        const adjustedData = { items: prov ? [prov] : [], totalCount: prov ? 1 : 0, pageNumber: 0, pageSize, totalPages: 1 };
        setProveedoresData(adjustedData);
        return;
      } catch (error: any) {
        if (error?.response?.status === 404) {
          setProveedoresData({ items: [], totalCount: 0, pageNumber: 0, pageSize, totalPages: 0 });
          return;
        }
        console.error('Error al buscar por nroProveedor:', error);
        setProveedoresData({ items: [], totalCount: 0, pageNumber: 0, pageSize, totalPages: 0 });
        return;
      }
    }
    try {
      const response = await api.get('/api/proveedores', { params: { pageNumber: pageNumber + 1, pageSize, searchTerm: rawSearch } });
      const adjusted = { ...response.data, pageNumber: response.data.pageNumber - 1 };
      setProveedoresData(adjusted);
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchProveedores(page, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm]);

  const handleChangePage = (event: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleDelete = async (nroProveedor: number) => {
    try {
      const check = await proveedorService.puedeEliminar(nroProveedor);
      if (!check.canDelete) {
        enqueueSnackbar(check.message || check.motivos?.[0] || 'No se puede eliminar el proveedor.', { variant: 'error' });
        return;
      }
      if (!window.confirm('¿Está seguro de eliminar este proveedor?')) return;
      await proveedorService.deleteProveedor(nroProveedor);
      enqueueSnackbar('Proveedor eliminado', { variant: 'success' });
      fetchProveedores(page, rowsPerPage, searchTerm);
    } catch (error) {
      console.error('Error al eliminar proveedor:', error);
      enqueueSnackbar(getDeleteErrorMessage(error), { variant: 'error' });
    }
  };

  const handleDescargarPlantilla = () => {
    try {
      const headers = [
        'nroProveedor', 'nombre', 'direccion', 'codigoCiudad', 'telefono1', 'telefono2', 'fax', 'email', 'tipoIva', 'cuit', 'dni',
        'contacto', 'descuento', 'porcCantB', 'nroCuenta', 'fechaAlta', 'fechaBaja', 'plazoEntrega', 'codigoRuta', 'saldo', 'condVta', 'tipoProveedor',
        'notas', 'notas2', 'activo', 'sitIB', 'esPersHumSucIndig', 'nroCuentaCompras', 'codigoRamoProv'
      ];
      const data = [headers, new Array(headers.length).fill('')];
      const ws = XLSX.utils.aoa_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Proveedores');
      const instrucciones = [
        ['Columna', 'Descripción', 'Valores aceptados'],
        ['nroProveedor', 'Número de proveedor', 'Número único (obligatorio)'],
        ['nombre', 'Nombre del proveedor', 'Texto (obligatorio)'],
        ['direccion', 'Dirección', 'Texto (opcional)'],
        ['codigoCiudad', 'Código de ciudad', 'Número (opcional)'],
        ['telefono1', 'Teléfono principal', 'Texto (opcional)'],
        ['telefono2', 'Teléfono secundario', 'Texto (opcional)'],
        ['fax', 'Fax', 'Texto (opcional)'],
        ['email', 'Email', 'Email válido (opcional)'],
        ['tipoIva', 'Tipo IVA', 'I=Inscripto, M=Monotributo, E=Exento, C=Consumidor (opcional)'],
        ['cuit', 'CUIT', 'Texto hasta 13 caracteres (opcional)'],
        ['dni', 'DNI', 'Número (opcional)'],
        ['contacto', 'Persona de contacto', 'Texto (opcional)'],
        ['descuento', 'Descuento', 'Número (opcional)'],
        ['porcCantB', 'Porcentaje de Conversión', 'Número 0–100 (opcional). Vacío o 0: no participa'],
        ['nroCuenta', 'Número de cuenta', 'Número (opcional)'],
        ['fechaAlta', 'Fecha de alta', 'Formato fecha (opcional)'],
        ['fechaBaja', 'Fecha de baja', 'Formato fecha (opcional)'],
        ['plazoEntrega', 'Plazo de entrega', 'Número (opcional)'],
        ['codigoRuta', 'Ruta / Zona (código)', 'Número (opcional). Si se omite se usará 1. Debe existir en el maestro de rutas'],
        ['saldo', 'Saldo', 'Número (opcional)'],
        ['condVta', 'Condición de venta', 'Texto (opcional)'],
        ['tipoProveedor', 'Tipo', 'C=Compra, G=General (opcional)'],
        ['notas', 'Nota 1 (uso interno)', 'Observaciones operativas de gestión interna: cheques, flete/sobres, plazos comerciales, etc. (opcional, máx. 1999). Disponible para impresión de OP tipo P'],
        ['notas2', 'Nota 2 (bancaria)', 'Texto libre para transferencia / Home Banking (opcional, máx. 1999)'],
        ['activo', 'Activo', 'Si/No o true/false (opcional)'],
        ['sitIB', 'Situación IB', 'Texto (opcional)'],
        ['esPersHumSucIndig', 'Persona humana/sucursal indígena', 'Si/No o true/false (opcional)'],
        ['nroCuentaCompras', 'Número cuenta compras', 'Número (opcional)'],
        ['codigoRamoProv', 'Ramo del proveedor', 'Código ramo existente (opcional)'],
      ];
      const wsInstrucciones = XLSX.utils.aoa_to_sheet(instrucciones);
      XLSX.utils.book_append_sheet(wb, wsInstrucciones, 'Instrucciones');
      XLSX.writeFile(wb, 'PlantillaProveedores.xlsx');
      enqueueSnackbar('✅ Plantilla Excel descargada correctamente', { variant: 'success', autoHideDuration: 4000 });
    } catch {
      enqueueSnackbar('❌ Error al crear la plantilla Excel', { variant: 'error', autoHideDuration: 4000 });
    }
  };

  const camposRequeridosApi = ['nroProveedor', 'nombre'];
  const camposApi = [
    'nroProveedor','nombre','direccion','codigoCiudad','telefono1','telefono2','fax','email','tipoIva','cuit','dni','contacto','descuento','porcCantB','nroCuenta','fechaAlta','fechaBaja','plazoEntrega','codigoRuta','saldo','condVta','tipoProveedor','notas','notas2','activo','sitIB','esPersHumSucIndig','nroCuentaCompras','codigoRamoProv'
  ];
  const camposNumericosApi = ['nroProveedor','dni','codigoCiudad','descuento','porcCantB','nroCuenta','plazoEntrega','codigoRuta','saldo','nroCuentaCompras'];
  const camposBooleanosApi = ['activo','esPersHumSucIndig'];
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
  const maxLengths: Record<string, number> = {
    nombre: 40, direccion: 25, telefono1: 60, telefono2: 60, fax: 60, email: 249, tipoIva: 1, cuit: 13,
    contacto: 40, descuento: 14, condVta: 12, tipoProveedor: 20, notas: 1999, notas2: 1999, activo: 20, codigoRamoProv: 20
  };

  const revisarLongitudes = (payload: Record<string, any>, fila: number): { fila: number; mensaje: string }[] => {
    const avisos: { fila: number; mensaje: string }[] = [];
    for (const [campo, max] of Object.entries(maxLengths)) {
      const val = payload[campo];
      if (val != null && typeof val === 'string' && val.length > max) {
        avisos.push({ fila, mensaje: `${campo} (${val.length} > ${max} caracteres) será truncado` });
      }
    }
    return avisos;
  };

  const construirPayloadDesdeRow = (rowNorm: Record<string, any>): Record<string, any> => {
    const proveedor: Record<string, any> = {};
    camposApi.forEach(key => {
      const lower = key.toLowerCase();
      let valor: any = rowNorm[lower];
      if (camposNumericosApi.includes(key)) {
        if (valor === undefined || valor === null || String(valor).trim() === '') { proveedor[key] = null; }
        else { proveedor[key] = Number(String(valor).replace(/[,]/g,'.').replace(/ /g,'')); }
      } else if (camposBooleanosApi.includes(key)) {
        const v = String(valor ?? '').toLowerCase().trim();
        proveedor[key] = v === 'true' || v === '1' || v === 'si' || v === 'sí' ? true : v === '' ? null : false;
      } else if (['fechaAlta','fechaBaja'].includes(key)) {
        proveedor[key] = (typeof valor === 'string' && valor.match(/^\d{4}-\d{2}-\d{2}/)) ? (new Date(valor + 'T00:00:00Z')).toISOString() : (valor ? (isNaN(new Date(valor).getTime()) ? null : new Date(valor).toISOString()) : null);
      } else if (key === 'email') {
        proveedor[key] = (valor === undefined || valor === null || valor === '') ? null : String(valor).trim().toLowerCase();
      } else {
        proveedor[key] = (valor === undefined || valor === null || valor === '') ? null : String(valor).trim();
      }
    });
    proveedor.nroProveedor = Number(proveedor.nroProveedor);
    if (
      proveedor.codigoRuta === null ||
      proveedor.codigoRuta === undefined ||
      Number(proveedor.codigoRuta) === 0
    ) {
      proveedor.codigoRuta = 1;
    }
    return {
      ...proveedor,
      nombre: String(proveedor.nombre || '').trim(),
      direccion: proveedor.direccion != null ? String(proveedor.direccion).trim() : null,
      telefono1: proveedor.telefono1 != null ? String(proveedor.telefono1) : null,
      telefono2: proveedor.telefono2 != null ? String(proveedor.telefono2) : null,
      fax: proveedor.fax != null ? String(proveedor.fax) : null,
      tipoIva: proveedor.tipoIva != null ? String(proveedor.tipoIva).trim() : null,
      cuit: proveedor.cuit != null ? String(proveedor.cuit).trim() : null,
      contacto: proveedor.contacto != null ? String(proveedor.contacto).trim() : null,
      descuento: proveedor.descuento != null ? String(proveedor.descuento).trim() : null,
      porcCantB:
        proveedor.porcCantB === null || proveedor.porcCantB === undefined || Number.isNaN(Number(proveedor.porcCantB))
          ? null
          : Number(proveedor.porcCantB),
      condVta: proveedor.condVta != null ? String(proveedor.condVta).trim() : null,
      tipoProveedor: String(proveedor.tipoProveedor || '').trim(),
      notas: proveedor.notas != null ? String(proveedor.notas).trim() : null,
      notas2: proveedor.notas2 != null ? String(proveedor.notas2).trim() : null,
      activo: proveedor.activo ? 'S' : 'N',
      sitIB: proveedor.sitIB != null ? String(proveedor.sitIB).trim() : null,
      esPersHumSucIndig: proveedor.esPersHumSucIndig ? 1 : 0,
      codigoRamoProv: proveedor.codigoRamoProv != null ? String(proveedor.codigoRamoProv).trim() : null
    };
  };

  const validarYConstruirPayloads = async (rows: any[]): Promise<{ payloadsValidos: any[]; errores: { fila: number; mensaje: string }[]; advertencias: { fila: number; mensaje: string }[] }> => {
    const payloadsValidos: any[] = [];
    const errores: { fila: number; mensaje: string }[] = [];
    const advertencias: { fila: number; mensaje: string }[] = [];
    const nroSet = new Set<string>();
    let pageNumber = 1;
    const pageSize = 20;
    let hasMore = true;
    while (hasMore) {
      const res = await proveedorService.getProveedores({ pageNumber, pageSize });
      const items = res.items ?? [];
      items.forEach(p => nroSet.add(String(p.nroProveedor)));
      hasMore = items.length === pageSize && (pageNumber * pageSize < (res.totalCount ?? 0));
      pageNumber++;
    }

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const rowNorm: Record<string, any> = {};
      Object.keys(row).forEach(k => { rowNorm[k.toLowerCase().trim()] = row[k]; });

      for (const campo of camposRequeridosApi) {
        const val = rowNorm[campo.toLowerCase()];
        if (val === undefined || val === null || String(val).trim() === '' || (campo === 'nroProveedor' && (isNaN(Number(String(val).replace(/[,]/g,'.').replace(/ /g,''))) || Number(val) === 0))) {
          errores.push({ fila: i + 2, mensaje: `Campo obligatorio "${campo}" vacío o inválido` });
          break;
        }
      }
      if (errores.some(e => e.fila === i + 2)) continue;

      for (const campoNum of camposNumericosApi) {
        const raw = rowNorm[campoNum.toLowerCase()];
        if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
          const normalized = typeof raw === 'string' ? raw.replace(/[,]/g,'.').replace(/ /g,'') : raw;
          if (isNaN(Number(normalized))) {
            errores.push({ fila: i + 2, mensaje: `La columna "${campoNum}" debe ser numérica` });
            break;
          }
          if (campoNum === 'porcCantB') {
            const n = Number(normalized);
            if (n < 0 || n > 100) {
              errores.push({ fila: i + 2, mensaje: 'porcCantB debe estar entre 0 y 100' });
              break;
            }
          }
        }
      }
      if (errores.some(e => e.fila === i + 2)) continue;

      const emailRaw = String(rowNorm['email'] ?? '').trim();
      if (emailRaw && !emailRegex.test(emailRaw)) {
        errores.push({ fila: i + 2, mensaje: `Email "${emailRaw}" inválido` });
        continue;
      }

      const nroProveedorExcel = Number(String(rowNorm['nroproveedor']).replace(/[,]/g,'.').replace(/ /g,''));
      if (nroSet.has(String(nroProveedorExcel))) {
        errores.push({ fila: i + 2, mensaje: `El proveedor ${nroProveedorExcel} ya existe` });
        continue;
      }

      try {
        const payload = construirPayloadDesdeRow(rowNorm);
        const avisos = revisarLongitudes(payload, i + 2);
        advertencias.push(...avisos);
        payloadsValidos.push(payload);
        nroSet.add(String(payload.nroProveedor));
      } catch (_) {
        errores.push({ fila: i + 2, mensaje: 'Error al construir el registro' });
      }
    }
    return { payloadsValidos, errores, advertencias };
  };

  const onImportChangeProveedores = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setValidacionImport(null);
    try {
      const fileBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(fileBuffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '', header: 0 });

      if (!rows.length) {
        enqueueSnackbar('El archivo está vacío.', { variant: 'warning', autoHideDuration: 4000 });
        return;
      }

      const headers = Object.keys(rows[0]).map(h => String(h).toLowerCase().trim());
      const columnasOpcionales = new Set(['porccantb']);
      const noEncontrados = camposApi.filter(
        (h) => !columnasOpcionales.has(h.toLowerCase()) && !headers.includes(h.toLowerCase())
      );
      if (noEncontrados.length > 0) {
        enqueueSnackbar('Columnas faltantes: ' + noEncontrados.join(', '), { variant: 'error', autoHideDuration: 6000 });
        return;
      }

      enqueueSnackbar('Validando datos del archivo...', { variant: 'info', autoHideDuration: 3000 });
      const { payloadsValidos, errores, advertencias } = await validarYConstruirPayloads(rows);
      setValidacionImport({
        payloadsValidos,
        errores,
        advertencias,
        totalFilas: rows.length
      });
      const msgAdvertencias = advertencias.length > 0 ? ` ${advertencias.length} advertencia(s) de longitud.` : '';
      enqueueSnackbar(
        errores.length === 0
          ? `Validación exitosa: ${payloadsValidos.length} registros listos para importar.${msgAdvertencias}`
          : `${payloadsValidos.length} válidos, ${errores.length} con errores. Revisa y confirma para importar los válidos.${msgAdvertencias}`,
        { variant: payloadsValidos.length > 0 ? 'info' : 'warning', autoHideDuration: 5000 }
      );
    } catch (error) {
      enqueueSnackbar('Error leyendo el archivo Excel (¿archivo corrupto/formato incorrecto?)', { variant: 'error', autoHideDuration: 9000 });
    }
  };

  const confirmarEImportar = async () => {
    const v = validacionImport;
    if (!v || v.payloadsValidos.length === 0) return;
    setImportando(true);
    let exitos = 0;
    let fallos = 0;
    try {
      for (let i = 0; i < v.payloadsValidos.length; i++) {
        try {
          await proveedorService.createProveedor(v.payloadsValidos[i]);
          exitos++;
        } catch (_) {
          fallos++;
        }
      }
      enqueueSnackbar(
        fallos > 0
          ? `Importación completada. Éxitos: ${exitos}. Fallos: ${fallos} (clave duplicada u otro error al guardar).`
          : `Importación completada. ${exitos} proveedor(es) creado(s).`,
        { variant: fallos > 0 ? 'warning' : 'success', autoHideDuration: 6000 }
      );
      fetchProveedores(page, rowsPerPage, searchTerm);
      setValidacionImport(null);
      setTimeout(() => { setOpenImportar(false); }, 800);
    } finally {
      setImportando(false);
    }
  };

  const cerrarDialogImportar = () => {
    setOpenImportar(false);
    setValidacionImport(null);
    setImportando(false);
  };

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', p: { xs: 2, sm: 3 } }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', p: 3 }}>
        {/* Header con controles */}
        <Box sx={{ mb: 3 }}>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            justifyContent="space-between" 
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: theme.palette.primary.main
              }}
            >
              Proveedores
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/compras/proveedores/nuevo')}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(21,128,61,0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(21,128,61,0.4)',
                  }
                }}
              >
                {isMobile ? 'Nuevo' : 'Nuevo Proveedor'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<AttachFileIcon />}
                onClick={() => setOpenImportar(true)}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                }}
              >
                Importar proveedores
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por nombre, CUIT o N° de proveedor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fafafa',
                  '&:hover': {
                    bgcolor: '#f5f5f5',
                  },
                  '&.Mui-focused': {
                    bgcolor: '#ffffff',
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>

      {/* Tabla o Cards según el tamaño de pantalla */}
      {isMobile ? (
        <Stack spacing={2}>
          {proveedoresData.items.map((prov) => (
            <Card key={prov.nroProveedor} sx={{ width: '100%', borderRadius: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                    {prov.nombre}
                  </Typography>
                  <Box>
                    <IconButton 
                      size="small"
                      onClick={() => navigate(`/compras/proveedores/${encodeURIComponent(prov.nroProveedor)}`)} 
                      color="primary"
                      sx={{
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: 'primary.light',
                          color: 'white',
                        },
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      size="small"
                      onClick={() => handleDelete(prov.nroProveedor)} 
                      color="error"
                      sx={{
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: 'error.light',
                          color: 'white',
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>N° Proveedor:</strong> {prov.nroProveedor}
                </Typography>
                {prov.cuit && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>CUIT:</strong> {prov.cuit}
                  </Typography>
                )}
                {prov.direccion && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Dirección:</strong> {prov.direccion}
                  </Typography>
                )}
                {prov.telefono1 && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Teléfono:</strong> {prov.telefono1}
                  </Typography>
                )}
                {prov.email && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Email:</strong> {prov.email}
                  </Typography>
                )}
                {prov.tipoIva && (
                  <Box sx={{ mb: 1 }}>
                    <strong>Tipo IVA: </strong>
                    <Chip
                      label={getTipoIvaChip(prov.tipoIva).label}
                      size="small"
                      sx={{
                        bgcolor: getTipoIvaChip(prov.tipoIva).bgColor,
                        color: getTipoIvaChip(prov.tipoIva).textColor,
                        fontWeight: 500,
                        height: 24,
                      }}
                    />
                  </Box>
                )}
                {prov.tipoProveedor && (
                  <Box>
                    <strong>Tipo: </strong>
                    <Chip
                      label={getTipoProveedorChip(prov.tipoProveedor).label}
                      size="small"
                      sx={{
                        bgcolor: getTipoProveedorChip(prov.tipoProveedor).bgColor,
                        color: getTipoProveedorChip(prov.tipoProveedor).textColor,
                        fontWeight: 500,
                        height: 24,
                      }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
          {proveedoresData.items.length === 0 && (
            <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
              No hay proveedores para mostrar
            </Typography>
          )}
          <TablePagination
            rowsPerPageOptions={[5, 10, 20]}
            component="div"
            count={proveedoresData.totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas por página"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
          />
        </Stack>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>N° Proveedor</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>CUIT</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Dirección</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Teléfono</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Tipo IVA</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {proveedoresData.items.map((prov) => (
                <TableRow 
                  key={prov.nroProveedor}
                  sx={{
                    '&:hover': {
                      bgcolor: '#f9f9f9',
                    },
                  }}
                >
                  <TableCell sx={{ fontWeight: 500 }}>{prov.nroProveedor}</TableCell>  
                  <TableCell>{prov.nombre}</TableCell>
                  <TableCell>{prov.cuit}</TableCell>
                  <TableCell>{prov.direccion}</TableCell>
                  <TableCell>{prov.telefono1}</TableCell>
                  <TableCell>{prov.email}</TableCell>
                  <TableCell>                    
                      <Chip
                        label={getTipoIvaChip(prov.tipoIva).label}
                        size="small"
                        sx={{
                          bgcolor: getTipoIvaChip(prov.tipoIva).bgColor,
                          color: getTipoIvaChip(prov.tipoIva).textColor,
                          fontWeight: 500,
                          height: 24,
                        }}
                      />
                  </TableCell>
                  <TableCell>
                    {prov.tipoProveedor ? (
                      <Chip
                        label={getTipoProveedorChip(prov.tipoProveedor).label}
                        size="small"
                        sx={{
                          bgcolor: getTipoProveedorChip(prov.tipoProveedor).bgColor,
                          color: getTipoProveedorChip(prov.tipoProveedor).textColor,
                          fontWeight: 500,
                          height: 24,
                        }}
                      />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell align="center">
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    <Tooltip title="Editar proveedor">
                      <IconButton 
                      onClick={() => navigate(`/compras/proveedores/${encodeURIComponent(prov.nroProveedor)}`)} 
                      size="small"
                      sx={{
                        bgcolor: '#2196f3',
                        color: '#fff',
                        borderRadius: 1.5,
                        width: 32,
                        height: 32,
                        '&:hover': {
                          bgcolor: '#14532d',
                          transform: 'scale(1.1)',
                        },
                        transition: 'all 0.2s'
                      }}
                    >
                      <EditIcon fontSize="small"/>
                    </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar proveedor">
                      <IconButton 
                      onClick={() => handleDelete(prov.nroProveedor)} 
                      size="small"
                      sx={{
                        bgcolor: '#f44336',
                        color: '#fff',
                        borderRadius: 1.5,
                        width: 32,
                        height: 32,
                        '&:hover': {
                          bgcolor: '#d32f2f',
                          transform: 'scale(1.1)',
                        },
                        transition: 'all 0.2s'
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                    </Tooltip>
                  </Stack>
                  </TableCell>
                </TableRow>
                ))}
              </TableBody>
            </Table>
              {proveedoresData.items.length === 0 && (
                <Box sx={{ py: 4 }}>
                  <Typography align="center" color="text.secondary">
                    No hay proveedores para mostrar
                  </Typography>
                </Box>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={proveedoresData.totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                sx={{
                  borderTop: '1px solid #e0e0e0',
                }}
              />
          </TableContainer>
      )}

      <Dialog open={openImportar} onClose={cerrarDialogImportar} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudUploadIcon sx={{ color: 'primary.main', fontSize: 30, mr: 1 }} />
            <Typography variant="h6" fontWeight={700}>Importar Proveedores</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box>
            {!validacionImport ? (
              <>
                <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, mb: 3, p: 3, background: '#fafbfd' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CloudDownloadIcon color="primary" sx={{ fontSize: 30, mr: 1 }} />
                    <Typography component="span" fontWeight={700} fontSize={18}>1. Descargar Plantilla</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 2 }}>Descarga la plantilla Excel con el formato correcto para importar proveedores.</Typography>
                  <Button variant="contained" color="info" fullWidth sx={{ fontWeight: 700, py: 1.2, mb: 1 }} startIcon={<CloudDownloadIcon />} onClick={handleDescargarPlantilla}>
                    Descargar Plantilla
                  </Button>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, p: 3, background: '#fafbfd' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CloudUploadIcon color="success" sx={{ fontSize: 30, mr: 1 }} />
                    <Typography component="span" fontWeight={700} fontSize={18}>2. Validar Archivo</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 2 }}>Selecciona tu archivo Excel. Se validará antes de importar. Solo se importarán los registros válidos tras tu confirmación.</Typography>
                  <label htmlFor="excel-upload-proveedores">
                    <input id="excel-upload-proveedores" type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={onImportChangeProveedores} />
                    <Button variant="contained" fullWidth color="success" startIcon={<CloudUploadIcon />} component="span" sx={{ fontWeight: 700, py: 1.2 }}>
                      Seleccionar y validar archivo
                    </Button>
                  </label>
                </Box>
              </>
            ) : (
              <Box sx={{ py: 1 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Resultado de la validación</Typography>
                <Stack spacing={1} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Chip label={`Total filas: ${validacionImport.totalFilas}`} color="default" size="small" />
                    <Chip label={`Válidos: ${validacionImport.payloadsValidos.length}`} color="success" size="small" />
                    {validacionImport.errores.length > 0 && (
                      <Chip label={`Con errores: ${validacionImport.errores.length}`} color="error" size="small" />
                    )}
                    {validacionImport.advertencias.length > 0 && (
                      <Chip label={`Advertencias: ${validacionImport.advertencias.length}`} color="warning" size="small" />
                    )}
                  </Box>
                </Stack>
                {validacionImport.advertencias.length > 0 && (
                  <Box sx={{ maxHeight: 140, overflow: 'auto', mb: 2, p: 1.5, bgcolor: '#fff8e1', borderRadius: 1, border: '1px solid #ffca28' }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 1 }}>Advertencias (campos que serán truncados al importar):</Typography>
                    {validacionImport.advertencias.slice(0, 12).map((a, idx) => (
                      <Typography key={idx} variant="body2" sx={{ display: 'block', color: '#e65100' }}>• Fila {a.fila}: {a.mensaje}</Typography>
                    ))}
                    {validacionImport.advertencias.length > 12 && (
                      <Typography variant="body2" color="text.secondary">… y {validacionImport.advertencias.length - 12} más</Typography>
                    )}
                  </Box>
                )}
                {validacionImport.errores.length > 0 && (
                  <Box sx={{ maxHeight: 180, overflow: 'auto', mb: 2, p: 1.5, bgcolor: '#fff3e0', borderRadius: 1 }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 1 }}>Filas con errores (no se importarán):</Typography>
                    {validacionImport.errores.slice(0, 15).map((e, idx) => (
                      <Typography key={idx} variant="body2" sx={{ display: 'block' }}>• Fila {e.fila}: {e.mensaje}</Typography>
                    ))}
                    {validacionImport.errores.length > 15 && (
                      <Typography variant="body2" color="text.secondary">… y {validacionImport.errores.length - 15} más</Typography>
                    )}
                  </Box>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {validacionImport.payloadsValidos.length > 0
                    ? `Haz clic en "Confirmar e importar" para crear los proveedores válidos.${validacionImport.advertencias.length > 0 ? ' Los campos que superen el límite se truncarán automáticamente.' : ''}`
                    : 'No hay registros válidos para importar. Corrige el archivo y vuelve a validar.'}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          {validacionImport ? (
            <>
              <Button onClick={() => setValidacionImport(null)} color="inherit" disabled={importando}>Volver a seleccionar</Button>
              <Button
                variant="contained"
                color="primary"
                onClick={confirmarEImportar}
                disabled={importando || validacionImport.payloadsValidos.length === 0}
              >
                {importando ? 'Importando...' : `Confirmar e importar ${validacionImport.payloadsValidos.length} proveedores`}
              </Button>
            </>
          ) : (
            <Button onClick={cerrarDialogImportar} color="inherit">Cancelar</Button>
          )}
        </DialogActions>
      </Dialog>
      </Card>
    </Box>
  );
};

export default Proveedores;


